import React, {useMemo, useState, useEffect} from "react";
import {Modal, Tabs, Tag, Empty, Alert} from "antd";
import {MessageCodes} from "../outputs/MessageCodes";
import {buildMessageMock, hasMessageMock} from "../../helpers/messageCodeMocks";

// Render components reach into useUser()/useAppContext() and may explode on
// fake data. We catch the error per-tab so the modal stays usable instead of
// taking the whole settings page down.
class PreviewBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {error: null};
    }
    static getDerivedStateFromError(error) {
        return {error};
    }
    componentDidCatch(error, info) {
        // swallow — surfaced in render
    }
    componentDidUpdate(prev) {
        if (prev.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({error: null});
        }
    }
    render() {
        if (this.state.error) {
            return (
                <Alert
                    type="warning"
                    showIcon
                    message="Preview failed to render with mock data."
                    description={String(this.state.error?.message || this.state.error)}
                />
            );
        }
        return this.props.children;
    }
}

function lookupEntries(code) {
    if (!code) return [];
    const norm = (s) => String(s || '').replace(/\s+/g, '').toUpperCase();
    const target = norm(code);
    const matches = [];
    for (const bucket of ['Internal', 'Compare']) {
        const map = MessageCodes[bucket] || {};
        for (const k of Object.keys(map)) {
            const entry = map[k];
            if (norm(entry.error_id) === target || norm(entry.error_id_text) === target) {
                matches.push({...entry, bucket, lookupKey: k});
            }
        }
    }
    return matches;
}

export default function MessageCodePreviewModal({open, code, onClose}) {
    const entries = useMemo(() => lookupEntries(code), [code]);
    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        if (entries.length > 0) setActiveTab(entries[0].lookupKey);
    }, [code, entries.length]);

    const tabItems = entries.map(entry => {
        const mock = buildMessageMock(entry.error_id, entry.key);
        const mocked = hasMessageMock(entry.error_id);
        const Component = entry.Component;

        return {
            key: entry.lookupKey,
            label: (
                <span>
                    {entry.error_id_text}
                    {entries.length > 1 && <Tag style={{marginLeft: 6}}>{entry.key}</Tag>}
                </span>
            ),
            children: (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12}}>
                        <Tag>Topic: {entry.key}</Tag>
                        {entry.key_text && entry.key_text !== entry.key && (
                            <Tag color="blue">Operation: {entry.key_text}</Tag>
                        )}
                        <Tag color={mocked ? 'green' : 'orange'}>
                            {mocked ? 'mock registered' : 'fallback placeholder'}
                        </Tag>
                    </div>
                    {!mocked && (
                        <Alert
                            type="info"
                            showIcon
                            message="No bespoke mock for this code yet — using a generic empty-row placeholder. Add an entry under src/helpers/mocks/."
                            style={{marginBottom: 4}}
                        />
                    )}
                    <div style={{border: '1px solid #f0f0f0', borderRadius: 4, padding: 12, background: '#fff'}}>
                        <PreviewBoundary resetKey={entry.lookupKey + (code || '')}>
                            {Component
                                ? <Component data={mock.data} message_key={mock.message_key}/>
                                : <Empty description="No component registered for this code"/>}
                        </PreviewBoundary>
                    </div>
                </div>
            ),
        };
    });

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1080}
            title={code ? `Message preview: ${code}` : 'Message preview'}
            destroyOnClose
        >
            {entries.length === 0 ? (
                <Empty description={`No MessageCodes entry registered for "${code}".`}/>
            ) : (
                <Tabs
                    type="card"
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                />
            )}
        </Modal>
    );
}
